import { Hotkey } from "@tanstack/react-hotkeys";
import {
  COMBOBOX_REMOVE_LAST_MENTION_HOTKEY,
  SESSION_CLOSE_ARTIFACT_HOTKEY,
} from "@/domains/keyboard-shortcuts/shared-hotkeys";

type TabNumberShortcutIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type PinnedItemShortcutIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type ShortcutAction =
  | "create_note"
  | "create_note_voice"
  | "search"
  | "open_mem_agent"
  | "show_heads_up"
  | "see_keyboard_shortcuts"
  | "organize_selected_notes"
  | "auto_organize_selected_notes"
  | "move_notes_to_trash"
  | "go_back"
  | "go_forward"
  | "go_home"
  | "go_collections"
  | "go_settings"
  | "close_tab"
  | `go_to_tab_${TabNumberShortcutIndex}`
  | `go_to_pinned_item_${PinnedItemShortcutIndex}`
  | "escape"
  | "next_pane"
  | "toggle_sidebar"
  | "focus_mode"
  | "editor_find"
  | "editor_find_next"
  | "find_in_note_previous_result_alternate"
  | "open_note_in_split_view"
  | "move_note_to_other_split_view"
  | "cleanup_note"
  | "toggle_voice_cleanup_panel"
  | "accept_voice_cleanup_changes"
  | "cleanup_and_auto_organize_note"
  | "copy_note_link"
  | "pin_note"
  | "quick_search_previous_result"
  | "quick_search_next_result"
  | "quick_search_select_highlighted_result"
  | "quick_search_close"
  | "notes_list_toggle_highlighted_selection"
  | "notes_list_extend_selection_to_highlighted"
  | "notes_list_highlight_previous"
  | "notes_list_highlight_previous_vim"
  | "notes_list_highlight_next"
  | "notes_list_highlight_next_vim"
  | "notes_list_open_highlighted"
  | "notes_list_open_highlighted_in_split_view"
  | "notes_list_toggle_highlighted_preview"
  | "onboarding_continue"
  | "find_in_note_previous_result"
  | "find_in_note_next_result"
  | "find_in_note_next_result_arrow"
  | "find_in_note_next_result_enter"
  | "find_in_note_close"
  | "organize_dialog_focus_next_result"
  | "organize_dialog_focus_previous_result"
  | "organize_dialog_escape"
  | "combobox_remove_last_mention"
  | "image_viewer_close"
  | "image_viewer_previous_image"
  | "image_viewer_next_image"
  | "image_viewer_zoom_in"
  | "image_viewer_zoom_in_alternate"
  | "image_viewer_zoom_out"
  | "image_viewer_zoom_out_alternate"
  | "pdf_viewer_find"
  | "pdf_viewer_close"
  | "pdf_viewer_previous_page"
  | "pdf_viewer_next_page"
  | "pdf_viewer_zoom_in"
  | "pdf_viewer_zoom_in_alternate"
  | "pdf_viewer_zoom_out"
  | "pdf_viewer_zoom_out_alternate"
  | "pdf_find_next_result"
  | "pdf_find_previous_result"
  | "pdf_find_close"
  | "selected_text_add_link"
  | "session_close_artifact";

export type ShortcutBindingAction =
  | ShortcutAction
  | "focus_previous_result"
  | "focus_next_result"
  | "escape_to_close"
  | "viewer_previous"
  | "viewer_next"
  | "viewer_zoom_in"
  | "viewer_zoom_in_alternate"
  | "viewer_zoom_out"
  | "viewer_zoom_out_alternate";

export type ShortcutPlatformBucket = "apple" | "pc";

export type HotkeyByPlatform = {
  apple: Hotkey;
  pc: Hotkey;
};

export type ShortcutBrowserFallback = true | Partial<HotkeyByPlatform>;

export type ShortcutSection =
  | "essentials"
  | "navigation"
  | "notes_list"
  | "editing"
  | "with_text_selected";

type ShortcutBindingSingle = {
  hotkey: Hotkey;
  browserFallback?: ShortcutBrowserFallback;
  hotkeyByPlatform?: never;
};

type ShortcutBindingSplit = {
  hotkeyByPlatform: HotkeyByPlatform;
  browserFallback?: ShortcutBrowserFallback;
  hotkey?: never;
};

export type ShortcutBinding = ShortcutBindingSingle | ShortcutBindingSplit;

type ShortcutRegistryEntry = {
  action: ShortcutAction;
  bindingAction?: ShortcutBindingAction;
  label: string;
  section: ShortcutSection;
  contextLabel?: string;
  searchHint?: string;
} & ShortcutBinding;

type ShortcutSectionConfig = {
  section: ShortcutSection;
  title: string;
};

export type KeyboardShortcutDisplaySuffixPart =
  | { kind: "text"; label: string }
  | { kind: "key"; label: string; borderless?: boolean };

export type KeyboardShortcutDisplayBinding = {
  binding: ShortcutBinding;
  suffixParts?: readonly KeyboardShortcutDisplaySuffixPart[];
};

export type KeyboardShortcutModalItem = {
  id: string;
  action?: ShortcutAction;
  bindingAction: ShortcutBindingAction | string;
  label: string;
  displayBindings: readonly KeyboardShortcutDisplayBinding[];
  contextLabel?: string;
  searchHint?: string;
};

export type KeyboardShortcutModalSection = {
  section: ShortcutSection;
  title: string;
  shortcuts: KeyboardShortcutModalItem[];
};

type KeyboardShortcutModalSectionsOptions = {
  all?: boolean;
  excludedActions?: ReadonlySet<ShortcutAction>;
};

const KEYBOARD_SHORTCUT_SECTION_CONFIG: readonly ShortcutSectionConfig[] = [
  { section: "essentials", title: "Essentials" },
  { section: "navigation", title: "Navigation" },
  { section: "notes_list", title: "Notes list" },
  { section: "editing", title: "Editing" },
  { section: "with_text_selected", title: "With text selected" },
];

const TAB_NUMBER_SHORTCUTS: readonly ShortcutRegistryEntry[] = (
  [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
).map(index => ({
  action: `go_to_tab_${index}`,
  label: `Go to tab ${index}`,
  section: "navigation",
  hotkey: `Mod+${index}` as Hotkey,
  browserFallback: {
    apple: `Control+${index}` as Hotkey,
    pc: `Control+${index}` as Hotkey,
  },
  searchHint: `switch to tab ${index}, tab ${index}`,
}));

const PINNED_ITEM_SHORTCUTS: readonly ShortcutRegistryEntry[] = (
  [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
).map(index => ({
  action: `go_to_pinned_item_${index}`,
  label: `Open pinned item ${index}`,
  section: "navigation",
  hotkeyByPlatform: {
    apple: `Mod+Control+${index}` as Hotkey,
    pc: `Control+Shift+${index}` as Hotkey,
  },
  searchHint: `open pinned item ${index}, pinned ${index}, sidebar pinned item ${index}`,
}));

const KEYBOARD_SHORTCUT_REGISTRY: readonly ShortcutRegistryEntry[] = [
  {
    action: "create_note",
    label: "New note",
    section: "essentials",
    hotkey: "Mod+N",
    browserFallback: true,
  },
  {
    action: "create_note_voice",
    label: "New note and start voice recording",
    section: "essentials",
    hotkey: "Mod+Shift+N",
    browserFallback: true,
    searchHint: "voice note, start voice recording, new note with voice",
  },
  {
    action: "search",
    label: "Search",
    section: "essentials",
    hotkey: "Mod+K",
  },
  {
    action: "open_mem_agent",
    label: "Open Mem",
    section: "essentials",
    hotkey: "Mod+J",
    searchHint: "chat, open Mem, attach selected notes, attach selected collections",
  },
  {
    action: "show_heads_up",
    label: "Toggle Heads Up",
    section: "essentials",
    hotkey: "Mod+Shift+K",
    searchHint: "related notes, connections, floating heads up",
  },
  {
    action: "see_keyboard_shortcuts",
    label: "See keyboard shortcuts",
    section: "essentials",
    hotkey: "Mod+/",
    searchHint: "keyboard shortcut modal, shortcuts help",
  },
  {
    action: "organize_selected_notes",
    label: "Organize note(s)",
    section: "essentials",
    hotkey: "Mod+O",
    searchHint: "collections, add to collection, selected notes, current note",
  },
  {
    action: "auto_organize_selected_notes",
    label: "Auto Organize note(s)",
    section: "essentials",
    hotkey: "Mod+Shift+O",
    browserFallback: {
      pc: "Alt+O",
    },
    searchHint: "automatic organize, AI organize, selected notes, current note",
  },
  {
    action: "move_notes_to_trash",
    label: "Move note(s) to trash",
    section: "essentials",
    hotkey: "Mod+Shift+Backspace",
    searchHint: "trash, move to trash, delete selected notes, delete active note",
  },
  {
    action: "quick_search_previous_result",
    bindingAction: "focus_previous_result",
    label: "Move to previous result",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "ArrowUp",
    searchHint: "quick search previous result, search modal previous result",
  },
  {
    action: "quick_search_next_result",
    bindingAction: "focus_next_result",
    label: "Move to next result",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "ArrowDown",
    searchHint: "quick search next result, search modal next result",
  },
  {
    action: "quick_search_select_highlighted_result",
    label: "Open highlighted result",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "Enter",
    searchHint: "quick search open result, search modal open result",
  },
  {
    action: "quick_search_close",
    bindingAction: "escape_to_close",
    label: "Close search",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "Escape",
    searchHint: "quick search close, search modal close",
  },
  {
    action: "onboarding_continue",
    label: "Continue",
    section: "essentials",
    contextLabel: "Onboarding",
    hotkey: "Enter",
    searchHint: "next onboarding step",
  },
  {
    action: "go_back",
    label: "Go back",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+[",
      pc: "Alt+ArrowLeft",
    },
  },
  {
    action: "go_forward",
    label: "Go forward",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+]",
      pc: "Alt+ArrowRight",
    },
  },
  {
    action: "go_home",
    label: "Go to Home",
    section: "navigation",
    hotkey: "Mod+Shift+H",
  },
  {
    action: "go_collections",
    label: "Go to Collections",
    section: "navigation",
    hotkey: "Mod+Shift+C",
  },
  {
    action: "go_settings",
    label: "Go to Settings",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+," as Hotkey,
      pc: "Control+," as Hotkey,
    },
    browserFallback: {
      apple: "Control+," as Hotkey,
      pc: "Control+," as Hotkey,
    },
  },
  {
    action: "close_tab",
    label: "Close tab",
    section: "navigation",
    hotkey: "Mod+W",
    browserFallback: true,
  },
  ...TAB_NUMBER_SHORTCUTS,
  ...PINNED_ITEM_SHORTCUTS,
  {
    action: "escape",
    label: "Escape",
    section: "navigation",
    hotkey: "Escape",
  },
  {
    action: "next_pane",
    label: "Next pane",
    section: "navigation",
    hotkey: "Mod+.",
    searchHint: "next split pane, switch pane, cycle panes",
  },
  {
    action: "toggle_sidebar",
    label: "Toggle left sidebar",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+Shift+\\" as Hotkey,
      pc: "Control+Alt+-" as Hotkey,
    },
    searchHint: "left sidebar, navigation sidebar",
  },
  {
    action: "focus_mode",
    label: "Toggle focused view",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+Control+\\" as Hotkey,
      pc: "Control+Alt+\\" as Hotkey,
    },
    searchHint: "toggle both sidebars, hide sidebars, show sidebars",
  },
  {
    action: "open_note_in_split_view",
    label: "Open note in split view",
    section: "navigation",
    hotkey: "Mod+Enter",
    searchHint: "inactive pane, split pane, second pane",
  },
  {
    action: "move_note_to_other_split_view",
    label: "Move note to other split pane",
    section: "navigation",
    hotkey: "Mod+Alt+T",
    searchHint: "move note to other pane, split pane, inactive pane",
  },
  {
    action: "image_viewer_close",
    bindingAction: "escape_to_close",
    label: "Close viewer",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "Escape",
  },
  {
    action: "image_viewer_previous_image",
    bindingAction: "viewer_previous",
    label: "Previous image",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "ArrowLeft",
  },
  {
    action: "image_viewer_next_image",
    bindingAction: "viewer_next",
    label: "Next image",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "ArrowRight",
  },
  {
    action: "image_viewer_zoom_in",
    bindingAction: "viewer_zoom_in",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "=",
  },
  {
    action: "image_viewer_zoom_in_alternate",
    bindingAction: "viewer_zoom_in_alternate",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "Mod+=",
  },
  {
    action: "image_viewer_zoom_out",
    bindingAction: "viewer_zoom_out",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "-",
  },
  {
    action: "image_viewer_zoom_out_alternate",
    bindingAction: "viewer_zoom_out_alternate",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "Mod+-",
  },
  {
    action: "pdf_viewer_find",
    label: "Find in PDF",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Mod+F",
  },
  {
    action: "pdf_viewer_close",
    bindingAction: "escape_to_close",
    label: "Close viewer",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Escape",
  },
  {
    action: "pdf_viewer_previous_page",
    bindingAction: "viewer_previous",
    label: "Previous page",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "ArrowLeft",
  },
  {
    action: "pdf_viewer_next_page",
    bindingAction: "viewer_next",
    label: "Next page",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "ArrowRight",
  },
  {
    action: "pdf_viewer_zoom_in",
    bindingAction: "viewer_zoom_in",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "=",
  },
  {
    action: "pdf_viewer_zoom_in_alternate",
    bindingAction: "viewer_zoom_in_alternate",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Mod+=",
  },
  {
    action: "pdf_viewer_zoom_out",
    bindingAction: "viewer_zoom_out",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "-",
  },
  {
    action: "pdf_viewer_zoom_out_alternate",
    bindingAction: "viewer_zoom_out_alternate",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Mod+-",
  },
  {
    action: "session_close_artifact",
    label: "Close artifact",
    section: "navigation",
    contextLabel: "Session artifact",
    hotkey: SESSION_CLOSE_ARTIFACT_HOTKEY,
  },
  {
    action: "notes_list_toggle_highlighted_selection",
    label: "Toggle highlighted item selection",
    section: "notes_list",
    hotkey: "X",
    searchHint: "notes list select highlighted item",
  },
  {
    action: "notes_list_extend_selection_to_highlighted",
    label: "Extend selection to highlighted item",
    section: "notes_list",
    hotkey: "Shift+X",
    searchHint: "notes list extend selection",
  },
  {
    action: "notes_list_highlight_previous",
    bindingAction: "focus_previous_result",
    label: "Highlight previous item",
    section: "notes_list",
    hotkey: "ArrowUp",
  },
  {
    action: "notes_list_highlight_previous_vim",
    label: "Highlight previous item",
    section: "notes_list",
    hotkey: "K",
    searchHint: "vim up, list previous item",
  },
  {
    action: "notes_list_highlight_next",
    bindingAction: "focus_next_result",
    label: "Highlight next item",
    section: "notes_list",
    hotkey: "ArrowDown",
  },
  {
    action: "notes_list_highlight_next_vim",
    label: "Highlight next item",
    section: "notes_list",
    hotkey: "J",
    searchHint: "vim down, list next item",
  },
  {
    action: "notes_list_open_highlighted",
    label: "Open highlighted item",
    section: "notes_list",
    hotkey: "Enter",
  },
  {
    action: "notes_list_open_highlighted_in_split_view",
    label: "Open highlighted item in split view",
    section: "notes_list",
    hotkey: "Mod+Enter",
  },
  {
    action: "notes_list_toggle_highlighted_preview",
    label: "Preview highlighted item",
    section: "notes_list",
    hotkey: "Space",
    searchHint: "quick look, note preview",
  },
  {
    action: "editor_find",
    label: "Find",
    section: "editing",
    hotkey: "Mod+F",
  },
  {
    action: "editor_find_next",
    label: "Find next",
    section: "editing",
    hotkey: "Mod+G",
  },
  {
    action: "find_in_note_previous_result_alternate",
    label: "Find previous result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Mod+E",
    searchHint: "find previous result, reverse search",
  },
  {
    action: "cleanup_note",
    label: "Clean up note",
    section: "editing",
    hotkey: "Mod+Shift+U",
    searchHint: "cleanup note, refine note, fix note",
  },
  {
    action: "toggle_voice_cleanup_panel",
    label: "Toggle Voice / Clean up panel",
    section: "editing",
    hotkey: "Mod+Shift+E",
    browserFallback: {
      pc: "Control+Alt+E",
    },
    searchHint: "voice panel, clean up panel, expand voice, collapse voice, cleanup panel",
  },
  {
    action: "accept_voice_cleanup_changes",
    label: "Accept Voice / Clean up changes",
    section: "editing",
    hotkey: "Mod+Shift+Y",
    browserFallback: {
      pc: "Control+Alt+Y",
    },
    searchHint: "accept voice changes, accept clean up changes, accept suggestion, yes",
  },
  {
    action: "cleanup_and_auto_organize_note",
    label: "Clean up and Auto Organize note",
    section: "editing",
    hotkey: "Mod+Shift+M",
    searchHint: "cleanup and organize note, refine and organize note",
  },
  {
    action: "copy_note_link",
    label: "Copy note link",
    section: "editing",
    hotkey: "Mod+L",
    browserFallback: {
      pc: "Control+Alt+L",
    },
    searchHint: "copy link, share note link, note URL",
  },
  {
    action: "pin_note",
    label: "Pin note",
    section: "editing",
    hotkey: "Mod+Shift+P",
    searchHint: "favorite note, pin to sidebar, unpin note",
  },
  {
    action: "find_in_note_previous_result",
    bindingAction: "focus_previous_result",
    label: "Find previous result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "ArrowUp",
  },
  {
    action: "find_in_note_next_result",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Mod+G",
  },
  {
    action: "find_in_note_next_result_arrow",
    bindingAction: "focus_next_result",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "ArrowDown",
  },
  {
    action: "find_in_note_next_result_enter",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Enter",
  },
  {
    action: "find_in_note_close",
    bindingAction: "escape_to_close",
    label: "Close find",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Escape",
  },
  {
    action: "organize_dialog_focus_next_result",
    bindingAction: "focus_next_result",
    label: "Move focus to next result",
    section: "editing",
    contextLabel: "Organize dialog",
    hotkey: "ArrowDown",
  },
  {
    action: "organize_dialog_focus_previous_result",
    bindingAction: "focus_previous_result",
    label: "Move focus to previous result",
    section: "editing",
    contextLabel: "Organize dialog",
    hotkey: "ArrowUp",
  },
  {
    action: "organize_dialog_escape",
    bindingAction: "escape_to_close",
    label: "Close or step back",
    section: "editing",
    contextLabel: "Organize dialog",
    hotkey: "Escape",
  },
  {
    action: "pdf_find_next_result",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in PDF",
    hotkey: "Enter",
  },
  {
    action: "pdf_find_previous_result",
    label: "Find previous result",
    section: "editing",
    contextLabel: "Find in PDF",
    hotkey: "Shift+Enter",
  },
  {
    action: "pdf_find_close",
    bindingAction: "escape_to_close",
    label: "Close find",
    section: "editing",
    contextLabel: "Find in PDF",
    hotkey: "Escape",
  },
  {
    action: "combobox_remove_last_mention",
    label: "Remove last mention",
    section: "editing",
    contextLabel: "Combobox input",
    hotkey: COMBOBOX_REMOVE_LAST_MENTION_HOTKEY,
  },
  {
    action: "selected_text_add_link",
    label: "Add link",
    section: "with_text_selected",
    contextLabel: "Text selected",
    hotkey: "Mod+K",
    searchHint: "add link, edit link, selected text link",
  },
];

const shortcutModalBinding = (hotkey: Hotkey): ShortcutBinding => ({ hotkey });
const shortcutModalTabRangeBinding = (index: TabNumberShortcutIndex): ShortcutBinding => ({
  hotkey: `Mod+${index}` as Hotkey,
  browserFallback: {
    apple: `Control+${index}` as Hotkey,
    pc: `Control+${index}` as Hotkey,
  },
});

const shortcutModalPinnedItemRangeBinding = (index: PinnedItemShortcutIndex): ShortcutBinding => ({
  hotkeyByPlatform: {
    apple: `Mod+Control+${index}` as Hotkey,
    pc: `Control+Shift+${index}` as Hotkey,
  },
});

const shortcutRangeSuffix = (endLabel: string): readonly KeyboardShortcutDisplaySuffixPart[] => [
  { kind: "key", label: "-", borderless: true },
  { kind: "key", label: endLabel },
];

const toShortcutBinding = (entry: ShortcutRegistryEntry): ShortcutBinding => {
  if (entry.hotkeyByPlatform !== undefined) {
    return {
      hotkeyByPlatform: entry.hotkeyByPlatform,
      browserFallback: entry.browserFallback,
    };
  }

  return {
    hotkey: entry.hotkey,
    browserFallback: entry.browserFallback,
  };
};

const getShortcutBindingAction = (entry: ShortcutRegistryEntry): ShortcutBindingAction =>
  entry.bindingAction ?? entry.action;

const SHORTCUT_REGISTRY_BY_ACTION = new Map<ShortcutAction, ShortcutRegistryEntry>(
  KEYBOARD_SHORTCUT_REGISTRY.map(entry => [entry.action, entry])
);

export const KEYBOARD_SHORTCUT_BINDINGS = KEYBOARD_SHORTCUT_REGISTRY.reduce<
  Record<ShortcutBindingAction, ShortcutBinding>
>(
  (bindings, entry) => {
    const bindingAction = getShortcutBindingAction(entry);
    if (bindings[bindingAction] !== undefined) {
      return bindings;
    }

    return {
      ...bindings,
      [bindingAction]: toShortcutBinding(entry),
    };
  },
  {} as Record<ShortcutBindingAction, ShortcutBinding>
);

export const getShortcutBinding = (
  action: ShortcutAction | ShortcutBindingAction
): ShortcutBinding | undefined => {
  const directBinding = KEYBOARD_SHORTCUT_BINDINGS[action as ShortcutBindingAction];
  if (directBinding) {
    return directBinding;
  }

  const entry = KEYBOARD_SHORTCUT_REGISTRY.find(shortcut => shortcut.action === action);
  if (!entry) {
    return undefined;
  }

  return KEYBOARD_SHORTCUT_BINDINGS[getShortcutBindingAction(entry)];
};

type KeyboardShortcutModalActionEntry = {
  action: ShortcutAction;
  id?: string;
  label?: string;
  searchHint?: string;
  displayBindings?: readonly KeyboardShortcutDisplayBinding[];
};

type KeyboardShortcutModalDisplayOnlyEntry = {
  id: string;
  label: string;
  binding: ShortcutBinding;
  searchHint?: string;
};

type KeyboardShortcutModalEntry =
  | KeyboardShortcutModalActionEntry
  | KeyboardShortcutModalDisplayOnlyEntry;

const KEYBOARD_SHORTCUT_MODAL_CONFIG: Readonly<
  Partial<Record<ShortcutSection, readonly KeyboardShortcutModalEntry[]>>
> = {
  essentials: [
    { action: "create_note" },
    {
      id: "create_note_via_paste",
      label: "New note via paste",
      binding: shortcutModalBinding("Mod+V"),
      searchHint: "paste, create note from clipboard",
    },
    { action: "create_note_voice", label: "New voice note" },
    { action: "search" },
  ],
  navigation: [
    { action: "go_forward" },
    { action: "go_back" },
    { action: "go_home" },
    { action: "go_collections" },
    { action: "go_settings" },
    { action: "close_tab" },
    { action: "escape", label: "Back or cancel" },
    {
      action: "go_to_tab_1",
      id: "go_to_tab_1_through_9",
      label: "Go to tab 1-9",
      searchHint: "go to tab 1 - 9, switch tabs by number, tab 1, tab 9",
      displayBindings: [
        {
          binding: shortcutModalTabRangeBinding(1),
          suffixParts: shortcutRangeSuffix("9"),
        },
      ],
    },
    {
      action: "go_to_pinned_item_1",
      id: "go_to_pinned_item_1_through_9",
      label: "Go to pinned item 1-9",
      searchHint:
        "open pinned item 1 - 9, pinned item 1, pinned item 9, pinned note, pinned collection",
      displayBindings: [
        {
          binding: shortcutModalPinnedItemRangeBinding(1),
          suffixParts: shortcutRangeSuffix("9"),
        },
      ],
    },
    { action: "next_pane", label: "Create or switch to other split pane" },
    { action: "open_mem_agent", label: "Show chat" },
    { action: "show_heads_up" },
    { action: "toggle_sidebar" },
    { action: "focus_mode" },
    { action: "see_keyboard_shortcuts" },
  ],
  notes_list: [
    {
      action: "notes_list_highlight_previous",
      label: "Go up",
      displayBindings: [
        { binding: shortcutModalBinding("ArrowUp") },
        { binding: shortcutModalBinding("K") },
      ],
    },
    {
      action: "notes_list_highlight_next",
      label: "Go down",
      displayBindings: [
        { binding: shortcutModalBinding("ArrowDown") },
        { binding: shortcutModalBinding("J") },
      ],
    },
    { action: "notes_list_toggle_highlighted_selection", label: "Select note" },
    {
      action: "notes_list_open_highlighted_in_split_view",
      label: "Open note in split view",
    },
    {
      action: "notes_list_toggle_highlighted_preview",
      label: "Preview note",
    },
    {
      id: "notes_list_select_multiple_notes",
      label: "Select multiple notes",
      binding: shortcutModalBinding("Shift+Click" as Hotkey),
      searchHint: "multi-select notes, select many notes",
    },
    { action: "organize_selected_notes", label: "Organize selected notes" },
    {
      action: "auto_organize_selected_notes",
      label: "Auto-organize selected notes",
    },
    { action: "open_mem_agent", label: "Start a chat with selected notes" },
    {
      action: "move_notes_to_trash",
      label: "Trash selected notes",
      displayBindings: [{ binding: shortcutModalBinding("Mod+Shift+Backspace") }],
    },
  ],
  editing: [
    { id: "editor_insert", label: "Insert", binding: shortcutModalBinding("/") },
    {
      id: "editor_mention_note",
      label: "Mention a note",
      binding: shortcutModalBinding("@" as Hotkey),
    },
    {
      id: "editor_mention_collection",
      label: "Mention a collection",
      binding: shortcutModalBinding("#" as Hotkey),
    },
    {
      action: "move_notes_to_trash",
      label: "Trash note",
      displayBindings: [{ binding: shortcutModalBinding("Mod+Shift+Backspace") }],
    },
    { action: "organize_selected_notes", label: "Organize note into collection" },
    {
      action: "auto_organize_selected_notes",
      label: "Auto-organize note into a collection",
    },
    { action: "cleanup_note", label: "Clean up" },
    { action: "toggle_voice_cleanup_panel", label: "Toggle Voice / Clean up panel" },
    { action: "accept_voice_cleanup_changes", label: "Accept Voice / Clean up changes" },
    { action: "copy_note_link" },
    { action: "editor_find", label: "Find in current note" },
    { action: "move_note_to_other_split_view", label: "Move current note to split view" },
  ],
  with_text_selected: [
    { id: "selected_text_bold", label: "Bold text", binding: shortcutModalBinding("Mod+B") },
    {
      id: "selected_text_italic",
      label: "Italicize text",
      binding: shortcutModalBinding("Mod+I"),
    },
    {
      id: "selected_text_strikethrough",
      label: "Strikethrough text",
      binding: shortcutModalBinding("Mod+Shift+X"),
    },
    { action: "selected_text_add_link", label: "Create link from selected text" },
    {
      id: "selected_text_paste_link",
      label: "Paste link on selected text",
      binding: shortcutModalBinding("Mod+V"),
    },
  ],
};

const KEYBOARD_SHORTCUT_MODAL_ALL_EXTRA_CONFIG: Readonly<
  Partial<Record<ShortcutSection, readonly KeyboardShortcutModalEntry[]>>
> = {
  essentials: [
    { action: "quick_search_previous_result" },
    { action: "quick_search_next_result" },
    { action: "quick_search_select_highlighted_result" },
    { action: "quick_search_close" },
    { action: "onboarding_continue" },
  ],
  navigation: [
    { action: "open_note_in_split_view" },
    { action: "image_viewer_close" },
    { action: "image_viewer_previous_image" },
    { action: "image_viewer_next_image" },
    { action: "image_viewer_zoom_in" },
    { action: "image_viewer_zoom_in_alternate" },
    { action: "image_viewer_zoom_out" },
    { action: "image_viewer_zoom_out_alternate" },
    { action: "pdf_viewer_find" },
    { action: "pdf_viewer_close" },
    { action: "pdf_viewer_previous_page" },
    { action: "pdf_viewer_next_page" },
    { action: "pdf_viewer_zoom_in" },
    { action: "pdf_viewer_zoom_in_alternate" },
    { action: "pdf_viewer_zoom_out" },
    { action: "pdf_viewer_zoom_out_alternate" },
    { action: "session_close_artifact" },
  ],
  notes_list: [
    { action: "notes_list_extend_selection_to_highlighted" },
    { action: "notes_list_highlight_previous_vim" },
    { action: "notes_list_highlight_next_vim" },
    { action: "notes_list_open_highlighted" },
  ],
  editing: [
    { action: "editor_find_next" },
    { action: "cleanup_and_auto_organize_note" },
    { action: "pin_note" },
    { action: "find_in_note_previous_result" },
    { action: "find_in_note_next_result" },
    { action: "find_in_note_next_result_arrow" },
    { action: "find_in_note_next_result_enter" },
    { action: "find_in_note_close" },
    { action: "organize_dialog_focus_next_result" },
    { action: "organize_dialog_focus_previous_result" },
    { action: "organize_dialog_escape" },
    { action: "pdf_find_next_result" },
    { action: "pdf_find_previous_result" },
    { action: "pdf_find_close" },
    { action: "combobox_remove_last_mention" },
  ],
};

export const getKeyboardShortcutModalSections = ({
  all = false,
  excludedActions,
}: KeyboardShortcutModalSectionsOptions = {}): KeyboardShortcutModalSection[] => {
  return KEYBOARD_SHORTCUT_SECTION_CONFIG.flatMap(({ section, title }) => {
    const modalEntries = KEYBOARD_SHORTCUT_MODAL_CONFIG[section];
    const allExtraEntries = all ? KEYBOARD_SHORTCUT_MODAL_ALL_EXTRA_CONFIG[section] : undefined;
    const entries = [...(modalEntries ?? []), ...(allExtraEntries ?? [])].filter(entry => {
      if (!("action" in entry)) {
        return true;
      }

      return !excludedActions?.has(entry.action);
    });
    if (entries.length === 0) {
      return [];
    }

    return [
      {
        section,
        title,
        shortcuts: entries.map(toKeyboardShortcutModalItem),
      },
    ];
  });
};

const toKeyboardShortcutModalItem = (
  modalEntry: KeyboardShortcutModalEntry
): KeyboardShortcutModalItem => {
  if ("action" in modalEntry) {
    const registryEntry = SHORTCUT_REGISTRY_BY_ACTION.get(modalEntry.action);
    if (!registryEntry) {
      throw new Error(`Unable to find keyboard shortcut action "${modalEntry.action}".`);
    }

    const binding = toShortcutBinding(registryEntry);

    return {
      id: modalEntry.id ?? registryEntry.action,
      action: registryEntry.action,
      bindingAction: getShortcutBindingAction(registryEntry),
      label: modalEntry.label ?? registryEntry.label,
      displayBindings: modalEntry.displayBindings ?? [{ binding }],
      contextLabel: registryEntry.contextLabel,
      searchHint: modalEntry.searchHint ?? registryEntry.searchHint,
    };
  }

  return {
    id: modalEntry.id,
    bindingAction: modalEntry.id,
    label: modalEntry.label,
    displayBindings: [{ binding: modalEntry.binding }],
    searchHint: modalEntry.searchHint,
  };
};
